import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@kidshome/ui';
import { Dialog } from '@/shared/ui';
import { useInstallPrompt } from './use-install-prompt';

/**
 * Install affordances UI (ADR-0018):
 *   - Chromium: a custom "Install app" button (only rendered when a deferred prompt was captured).
 *   - iOS Safari: the same "Install app" button, which opens a dismissible "Add to Home Screen" guide
 *     in the shared accessible Dialog (focus trap, Escape, scrim click) — iOS can't prompt
 *     programmatically, and we never auto-open the guide over a child's session. An explicit
 *     "Not now" is remembered so we don't keep offering.
 * Mounted once in the app shell; renders nothing when already installed or unsupported.
 */
export function InstallAffordance(): React.ReactElement | null {
  const { t } = useTranslation(['pwa', 'common']);
  const { canPromptInstall, promptInstall, showIosInstall, dismissIosGuide } = useInstallPrompt();
  const [guideOpen, setGuideOpen] = useState(false);

  if (showIosInstall) {
    return (
      <>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setGuideOpen(true)}
          aria-label={t('install.button')}
        >
          {t('install.button')}
        </Button>
        <Dialog open={guideOpen} onClose={() => setGuideOpen(false)} title={t('install.iosTitle')}>
          <ol className="flex flex-col gap-2 text-sm text-text-muted">
            <li>{t('install.iosStep1')}</li>
            <li>{t('install.iosStep2')}</li>
            <li>{t('install.iosStep3')}</li>
          </ol>
          {/* Escape/scrim just close (the button stays for later); "Not now" remembers the choice. */}
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              dismissIosGuide();
              setGuideOpen(false);
            }}
          >
            {t('install.dismiss')}
          </Button>
        </Dialog>
      </>
    );
  }

  if (canPromptInstall) {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={() => void promptInstall()}
        aria-label={t('install.button')}
      >
        {t('install.button')}
      </Button>
    );
  }

  return null;
}
